import * as React from 'react'
import { inject, observer } from 'mobx-react'
import Router from 'next/router'
import { autorun } from 'mobx'
import { trackAffirmLearnMoreClick, trackInitiateCheckout } from 'events/index'
import { SKIP_MEMBERSHIP_CHECKOUT_KEY } from 'constants/index'
import { AffirmLocations } from 'constants/events'
import { setPageScroll } from 'utils'
import { isMembershipProductId, isMemberUpgradeProductId, isPodBedVariantId, isPodCoverProductId, isPodCoverVariantId } from 'products/utils'
import { RootStore } from 'stores'
import { Cart } from 'components/Cart/Cart'
import { CartLineItem, LineItem, NewCartStore } from 'cart'
import { ClientOnly } from 'components/ClientOnly/ClientOnly'
import { BundleProducts, MembershipProducts, PillowProducts, PodCoverProducts } from 'products/allProducts'

interface State {
	isLoading: boolean
	mounted: boolean
}

export interface CartProps {
	rootStore?: RootStore
}

class CartSection extends React.Component<CartProps, State> {
	constructor(props: CartProps) {
		super(props)
		this.state = {
			isLoading: false,
			mounted: false,
		}

		this.onCheckoutClick = this.onCheckoutClick.bind(this)
	}

	componentDidMount() {
		autorun(() => {
			this.setState({
				mounted: true,
			})
			if (this.props.rootStore.cartStoreNew.isCartOpen) {
				// TODO WEB AUDIT this causes safari to highlight the close button
				document.getElementById('cart-close-link')?.focus()
			}
		})
	}

	handleIsLoading = (loading: boolean) => {
		this.setState({
			isLoading: loading,
		})
	}

	openFinancingModal() {
		trackAffirmLearnMoreClick(AffirmLocations.CART)
		const affirm = document.querySelector('.affirm-trigger-container .affirm-modal-trigger') as any
		if (affirm.click) {
			affirm.click()
		}
	}

	onCheckoutClick = (e: Event, redirectToPlanSelection: boolean) => {
		const { cartStoreNew } = this.props.rootStore

		e.preventDefault()

		if (redirectToPlanSelection) {
			const path = this.props.rootStore.settingsStore.currentRegion ? `/${this.props.rootStore.settingsStore.currentRegion}/` : '/'
			Router.push(
				{
					pathname: path,
				},
				path
			).then()

			cartStoreNew.toggleCartOpen()
			return
		} else {
			trackInitiateCheckout(cartStoreNew.getFullCartItems())
			setPageScroll(true)

			cartStoreNew.redirectToCheckout()
		}
	}

	removeItem = (item: CartLineItem) => {
		const { cartStoreNew } = this.props.rootStore!
		let itemsToRemove: LineItem[] = []

		// If we're removing the Pod from cart also remove 8PLUS membership
		const productNeedsMembership = isPodCoverProductId(item.productId)

		const shouldRemoveMembership = productNeedsMembership && cartStoreNew.numCovers < 2

		if (shouldRemoveMembership) {
			const allPlans = Object.values(MembershipProducts).map((o) => {
				return {
					variantId: o.variants.standard.id,
					quantity: 1,
					attributes: [],
				}
			})
			itemsToRemove = [...itemsToRemove, ...allPlans]
		}

		itemsToRemove.push(item)

		const cartDiscountedMattresses = cartStoreNew.getCartDiscountedMattresses()
		if (cartDiscountedMattresses.length > 0 && cartStoreNew.numCovers === 1 && isPodCoverProductId(item.productId)) {
			itemsToRemove.push(
				...cartDiscountedMattresses.map((it) => ({
					variantId: it.variantId,
					quantity: it.quantity,
					attributes: [],
				}))
			)
		}

		cartStoreNew.removeItems(itemsToRemove).then(this.checkAccessoriesDiscount)
	}

	updateItem = (item: CartLineItem, action: string) => {
		const { cartStoreNew } = this.props.rootStore!
		const itemsToUpdate: LineItem[] = [item]
		cartStoreNew.updateItems(itemsToUpdate, action).then(this.checkAccessoriesDiscount)
	}

	checkAccessoriesDiscount = () => {
		// ACCESS20 - 20% off the accessories with pod
		const { cartStoreNew, promoStore } = this.props.rootStore!
		if ((!cartStoreNew.hasPodOrCover || cartStoreNew.numAccessories === 0) && promoStore.hasPromo('ACCESS20')) {
			promoStore.removePromo()
		}
	}

	private calculateSavings(cartItems: CartLineItem[], cartStoreNew: NewCartStore) {
		let saleSavings = 0
		let builtInCompareSavings = 0

		for (const item of cartItems) {
			const productVariantSaving = item.comparePrice ? item.comparePrice - item.price : 0
			if (isMemberUpgradeProductId(item.productId) || item.productId === BundleProducts.SleepEssentialBundle.id) {
				builtInCompareSavings = builtInCompareSavings + productVariantSaving * item.quantity
			}
			saleSavings = saleSavings + productVariantSaving * item.quantity
		}

		const priceAfterSavings = cartStoreNew.total + builtInCompareSavings || 0
		const priceBeforeSavings = priceAfterSavings + saleSavings
		return { priceAfterSavings, priceBeforeSavings }
	}

	private getCartLineItems(cartStoreNew: NewCartStore) {
		const cartItems: LineItem[] = []
		let containsThermoCover = false
		let containsPodBed = false
		cartStoreNew.cart.lineItems.forEach((item) => {
			const isThermoCover = isPodCoverVariantId(item.variantId)
			const isPodFamily = isPodBedVariantId(item.variantId)

			if (isThermoCover && item.quantity > 0) {
				containsThermoCover = true
			}
			if (isPodFamily && item.quantity > 0) {
				containsPodBed = true
			}

			if (isPodFamily || isThermoCover) {
				cartItems.unshift(item)
			} else {
				cartItems.push(item)
			}
		})
		return { cartItems, containsThermoCover, containsPodBed }
	}

	render() {
		const { cartStoreNew, settingsStore, priceStore, productStore } = this.props.rootStore

		const cartResult = this.getCartLineItems(cartStoreNew)
		let cartItems = cartStoreNew.getFullCartItems()
		const containsThermoCover = cartResult.containsThermoCover
		const containsPodBed = cartResult.containsPodBed

		let trialNights = 0
		if (cartStoreNew.numPods > 0) trialNights = 30
		if (cartStoreNew.hasTechCover && cartStoreNew.numPods === 0) trialNights = 30

		let legaleseText = ''
		if (settingsStore.settings && settingsStore.settings.cartLegalese && (containsPodBed || containsThermoCover)) {
			legaleseText = legaleseText + settingsStore.settings.cartLegalese
		} else if (settingsStore.currentRegion === '' || settingsStore.currentRegion === 'ca') {
			legaleseText = '*Financing subject to approval.'
		}

		const isAllowedWithoutMembership = process.browser ? sessionStorage.getItem(SKIP_MEMBERSHIP_CHECKOUT_KEY) != null : false
		const redirectToPlanSelection = containsThermoCover && !cartStoreNew.hasMembership && !isAllowedWithoutMembership && !cartStoreNew.hasMemberUpgradeInCart

		// const discounts = discountCodes[promoStore.discountCode]
		// const globalDiscounts = discountCodes[settingsStore.globalPromoCode]

		// if (discounts) {
		// 	cartItems = cartItems.map((item) => priceStore.priceManager.discountCartItem(item, discounts[item.variantId], globalDiscounts?.[item.variantId], priceStore.currency))
		// }

		cartItems = cartItems.filter((item) => !!item)

		const nonMembershipItems = cartItems.filter((item) => !isMembershipProductId(item.productId))
		const membershipItems = cartItems.filter((item) => isMembershipProductId(item.productId))
		cartItems = [...nonMembershipItems, ...membershipItems]

		const cartDescriptions: string[][] = []
		for (const item of cartItems) {
			const product = productStore.productManager.getProductFromID(item.productId)
			if (product) {
				cartDescriptions.push(productStore.productManager.getCartDescription(product))
			}
		}

		const { priceBeforeSavings, priceAfterSavings } = this.calculateSavings(cartItems, cartStoreNew)
		const priceAdjustment = 0 // containsDelayedSubscription ? -18000 : 0

		const upsellProducts = [PodCoverProducts.Pod3CoverPerfect, BundleProducts.SleepEssentialBundle, PillowProducts.CarbonAirPillow].map((p) => priceStore.withPrices(p))

		return (
			<ClientOnly>
				<Cart
					isLoading={cartStoreNew.showInterstitial}
					isUpdating={cartStoreNew.updatingCart}
					currency={priceStore.currency}
					open={cartStoreNew.isCartOpen}
					checkoutButtonText={redirectToPlanSelection ? 'Select plan and checkout' : 'Go to checkout'}
					cartTotal={priceAfterSavings + priceAdjustment}
					cartCompareTotal={priceBeforeSavings + priceAdjustment}
					trialLength={trialNights}
					items={cartItems}
					cartDescriptions={cartDescriptions}
					legalese={legaleseText}
					onCloseCart={() => cartStoreNew.toggleCartOpen()}
					onCheckout={() => this.onCheckoutClick(new UIEvent('click'), redirectToPlanSelection)}
					openFinancing={() => this.openFinancingModal()}
					onRemoveItem={this.removeItem}
					updateItem={this.updateItem}
					showSubtotals={false}
					saleEndDate={settingsStore.saleEndDate}
					hideWarranty
					currentMembership={cartStoreNew.getIndexOfCurrentMembership()}
					swapPlan={cartStoreNew.swapPlan}
					upsellProducts={upsellProducts}
				/>
			</ClientOnly>
		)
	}
}

export default inject('rootStore')(observer(CartSection))
